import axiosInstance from '../services/ullrApi/axiosInstance';

/**
 * GET a path on the Ullr API through the app's axios instance (base URL,
 * credentials, and the redirect to /login on a 401) and resolve to the body.
 *
 * Rejects when there is no body to give: the instance's interceptor resolves a
 * cancelled request to `{}` and a 401 (while it redirects) to `undefined`, and
 * handing either to SWR as data would look like a successful empty answer.
 */
export async function apiGet<T>(path: string): Promise<T> {
  const response = await axiosInstance.get<T>(path);
  if (!response || !('data' in response)) {
    throw new Error(`No response body for GET ${path}`);
  }
  return response.data;
}

/** The HTTP status of a failed request, when there was one (none for a network error). */
export function errorStatus(error: unknown): number | undefined {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * Whether SWR should retry a failed fetch on its error-retry timer. A 4xx is an
 * answer (403: no access, 404: no such thing / a backend without the route) and
 * asking again on a timer can't change it; a 5xx or a network error may be
 * transient. Focus / reconnect revalidation still re-asks after a 4xx, so a
 * user granted access later sees the data without a reload.
 */
export function isRetryableError(error: unknown): boolean {
  const status = errorStatus(error);
  return status === undefined || status >= 500;
}
